import React, { useEffect, useState } from "react";
import api, { formatApiError, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { ExternalLink, Upload, Trash2, Film } from "lucide-react";

const FIELDS = [
  { key: "hero_title", label: "Hero title" },
  { key: "hero_subtitle", label: "Hero subtitle", type: "textarea" },
  { key: "hero_image_url", label: "Hero image URL" },
  { key: "hero_video_url", label: "Hero video URL (optional)" },
  { key: "about_text", label: "About paragraph", type: "textarea" },
  { key: "contact_email", label: "Contact email" },
  { key: "contact_phone", label: "Contact phone" },
  { key: "office_address", label: "Office address" },
  { key: "business_hours", label: "Business hours" },
  { key: "whatsapp_number", label: "WhatsApp number" },
  { key: "social_instagram", label: "Instagram URL" },
  { key: "social_linkedin", label: "LinkedIn URL" },
  { key: "social_facebook", label: "Facebook URL" },
  { key: "google_maps_embed", label: "Google Maps embed URL" },
];

export default function PortalSiteContent() {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/site/content").then((r) => setForm(r.data || {})).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.id; delete payload.updated_at;
      await api.put("/site/content", payload);
      toast.success("Website content updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const uploadHero = async (file) => {
    const fd = new FormData();
    fd.append("file", file); fd.append("kind", "hero");
    const { data } = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return `/api/media/${data.storage_path}`;
  };

  const [videoUploading, setVideoUploading] = useState(false);
  const [presets, setPresets] = useState([]);

  useEffect(() => {
    api.get("/site/portal-video/presets").then((r) => setPresets(r.data || [])).catch(() => {});
  }, []);

  const applyPreset = (p) => {
    setForm((cur) => ({
      ...cur,
      portal_video_url: p.url,
      portal_video_enabled: true,
    }));
    toast.success(`Preset applied — remember to Save changes.`);
  };

  const uploadPortalVideo = async (file) => {
    if (!file) return;
    const okType = /^video\/(mp4|webm)$/.test(file.type) || /\.(mp4|webm)$/i.test(file.name);
    if (!okType) { toast.error("Video must be MP4 or WebM"); return; }
    setVideoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/site/portal-video", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((cur) => ({
        ...cur,
        portal_video_url: data.portal_video_url,
        portal_video_enabled: true,
      }));
      toast.success("Portal video uploaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally { setVideoUploading(false); }
  };

  const removePortalVideo = async () => {
    if (!window.confirm("Remove the portal background video?")) return;
    try {
      await api.delete("/site/portal-video");
      setForm((cur) => ({ ...cur, portal_video_url: "", portal_video_enabled: false }));
      toast.success("Portal video removed");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-6 lg:p-12 max-w-4xl" data-testid="portal-site-content">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
        <div>
          <div className="eyebrow mb-1">Admin</div>
          <h1 className="font-heading font-light text-3xl lg:text-4xl tracking-tighter">Website content</h1>
          <p className="text-stone mt-2 text-sm">Edit the copy, hero media and contact details shown on the public site.</p>
        </div>
        <div className="flex items-center gap-2">
          <a href="/" target="_blank" rel="noreferrer" className="text-xs uppercase tracking-widest border border-line px-3 py-2 inline-flex items-center gap-2 hover:border-ink" data-testid="site-preview">
            <ExternalLink size={12} /> Preview site
          </a>
          <Button onClick={save} disabled={saving} className="bg-ink text-white hover:bg-ink/90 rounded-none" data-testid="site-save-btn">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>

      <div className="space-y-6">
        {/* Branding — logo & favicon */}
        <section className="border border-line bg-white p-6" data-testid="branding-settings">
          <div className="mb-4">
            <div className="eyebrow mb-1">Website branding</div>
            <h2 className="font-heading text-xl tracking-tight">Logo &amp; favicon</h2>
            <p className="text-sm text-stone mt-1">
              Uploaded logo replaces the wordmark in the public navigation. Favicon appears in browser tabs. Transparent PNG or SVG recommended.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <BrandingUploader
              label="Logo (dark mode)"
              value={form.logo_url}
              onChange={(url) => setForm({ ...form, logo_url: url })}
              testId="logo"
              previewBg="bg-ink"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              recommend="Shown on the dark hero. Transparent PNG/SVG, light-coloured."
            />
            <BrandingUploader
              label="Logo (light mode)"
              value={form.logo_url_light}
              onChange={(url) => setForm({ ...form, logo_url_light: url })}
              testId="logo-light"
              previewBg="bg-white"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              recommend="Shown on all light backgrounds. Optional — falls back to a black silhouette of the dark logo."
            />
            <BrandingUploader
              label="Favicon"
              value={form.favicon_url}
              onChange={(url) => setForm({ ...form, favicon_url: url })}
              testId="favicon"
              previewBg="bg-sand"
              accept="image/png,image/x-icon,image/svg+xml,.ico"
              recommend="Square PNG/ICO/SVG, 32×32 or 64×64"
              small
            />
          </div>

          {/* Logo size */}
          <div className="mt-6 pt-6 border-t border-line max-w-md">
            <div className="flex justify-between items-center mb-2">
              <Label className="eyebrow">Logo size on home page</Label>
              <span className="font-mono text-xs text-stone">{form.logo_size ?? 64}px</span>
            </div>
            <Slider
              data-testid="logo-size-slider"
              min={24}
              max={160}
              step={2}
              value={[form.logo_size ?? 64]}
              onValueChange={(v) => setForm({ ...form, logo_size: v[0] })}
            />
            <p className="text-xs text-stone mt-2">
              Height in pixels. Only applies when a logo image is uploaded. Save changes to publish.
            </p>
          </div>
        </section>

        {/* Services tiles */}
        <section className="border border-line bg-white p-6" data-testid="services-settings">
          <div className="mb-4">
            <div className="eyebrow mb-1">Home page</div>
            <h2 className="font-heading text-xl tracking-tight">Service tiles</h2>
            <p className="text-sm text-stone mt-1">
              The three service cards shown on the home page. Update image, tagline, title and description.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {(form.services || []).map((svc, idx) => (
              <ServiceCardEditor
                key={svc.key || idx}
                index={idx}
                value={svc}
                onChange={(patch) => {
                  const next = [...form.services];
                  next[idx] = { ...next[idx], ...patch };
                  setForm({ ...form, services: next });
                }}
              />
            ))}
          </div>
        </section>

        {/* Portal Background Video (Super Admin) */}
        <section className="border border-ink bg-white p-6" data-testid="portal-video-settings">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <div className="eyebrow mb-1 inline-flex items-center gap-2"><Film size={12} /> Portal background</div>
              <h2 className="font-heading text-xl tracking-tight">Portal background video</h2>
              <p className="text-sm text-stone mt-1 max-w-xl">
                Upload an abstract art video (MP4 or WebM, max 60&nbsp;MB) that plays behind the portal landing page. Autoplays, muted and looped. Kept subtle with a white overlay above.
              </p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <Label className="eyebrow" htmlFor="portal-video-enabled">Enable</Label>
              <Switch
                id="portal-video-enabled"
                checked={!!form.portal_video_enabled}
                onCheckedChange={(v) => setForm({ ...form, portal_video_enabled: v })}
                data-testid="portal-video-enabled"
              />
            </div>
          </div>

          {form.portal_video_url ? (
            <div className="relative bg-black aspect-video mb-4 overflow-hidden border border-line">
              <video
                key={form.portal_video_url}
                src={form.portal_video_url}
                autoPlay muted loop playsInline
                className="w-full h-full object-cover"
                data-testid="portal-video-preview"
              />
            </div>
          ) : (
            <div className="border border-dashed border-line bg-sand/50 aspect-video mb-4 flex items-center justify-center text-stone">
              <div className="text-center">
                <Film size={28} strokeWidth={1.2} className="mx-auto mb-2" />
                <div className="text-sm">No background video uploaded</div>
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3 mb-6">
            <label className="cursor-pointer border border-ink bg-ink text-white px-4 py-2 text-sm inline-flex items-center gap-2 hover:bg-ink/90" data-testid="portal-video-upload">
              <Upload size={14} /> {videoUploading ? "Uploading…" : (form.portal_video_url ? "Replace video" : "Upload video")}
              <input
                type="file"
                accept="video/mp4,video/webm,.mp4,.webm"
                hidden
                disabled={videoUploading}
                onChange={(e) => uploadPortalVideo(e.target.files?.[0])}
              />
            </label>
            {form.portal_video_url && (
              <Button
                variant="outline"
                onClick={removePortalVideo}
                data-testid="portal-video-remove"
                className="rounded-none border-line text-destructive hover:bg-destructive hover:text-white"
              >
                <Trash2 size={14} className="mr-2" /> Remove
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label className="eyebrow">Video opacity</Label>
                <span className="font-mono text-xs text-stone">
                  {Math.round((form.portal_video_opacity ?? 0.35) * 100)}%
                </span>
              </div>
              <Slider
                data-testid="portal-video-opacity"
                min={0} max={1} step={0.05}
                value={[form.portal_video_opacity ?? 0.35]}
                onValueChange={(v) => setForm({ ...form, portal_video_opacity: v[0] })}
              />
              <p className="text-xs text-stone mt-2">Higher = more visible video.</p>
            </div>
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label className="eyebrow">White overlay opacity</Label>
                <span className="font-mono text-xs text-stone">
                  {Math.round((form.portal_overlay_opacity ?? 0.75) * 100)}%
                </span>
              </div>
              <Slider
                data-testid="portal-overlay-opacity"
                min={0} max={1} step={0.05}
                value={[form.portal_overlay_opacity ?? 0.75]}
                onValueChange={(v) => setForm({ ...form, portal_overlay_opacity: v[0] })}
              />
              <p className="text-xs text-stone mt-2">Lower = video more prominent; higher = softer, faded.</p>
            </div>
            <div className="md:col-span-2">
              <div className="flex justify-between items-center mb-2">
                <Label className="eyebrow">Playback speed</Label>
                <span className="font-mono text-xs text-stone">
                  {(form.portal_video_playback_rate ?? 1).toFixed(2)}×
                </span>
              </div>
              <Slider
                data-testid="portal-video-speed"
                min={0.25} max={2} step={0.05}
                value={[form.portal_video_playback_rate ?? 1]}
                onValueChange={(v) => setForm({ ...form, portal_video_playback_rate: v[0] })}
              />
              <p className="text-xs text-stone mt-2">Slower speeds feel more architectural and cinematic.</p>
            </div>
          </div>

          {/* Curated presets */}
          {presets.length > 0 && (
            <div className="mt-8 pt-6 border-t border-line">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="eyebrow">Curated loops</div>
                  <p className="text-xs text-stone mt-1">Pick a preset — no upload required.</p>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="portal-video-presets">
                {presets.map((p) => {
                  const active = form.portal_video_url === p.url;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => applyPreset(p)}
                      data-testid={`preset-${p.id}`}
                      className={`group relative aspect-video overflow-hidden border transition-all text-left ${
                        active ? "border-ink border-[1.5px]" : "border-line hover:border-ink"
                      }`}
                    >
                      {p.thumbnail && (
                        <img src={p.thumbnail} alt={p.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity" />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                      <div className="absolute inset-x-0 bottom-0 p-2 text-white">
                        <div className="font-cg text-[10px] tracking-[0.28em] uppercase">{p.name}</div>
                      </div>
                      {active && (
                        <div className="absolute top-2 right-2 bg-ink text-white text-[9px] uppercase tracking-widest px-2 py-0.5">
                          Selected
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-stone mt-3">
                Selecting a preset sets the video URL. Click <strong className="text-ink">Save changes</strong> above to apply.
              </p>
            </div>
          )}

          <div className="mt-6 text-xs text-stone">
            Remember to <strong className="text-ink">Save changes</strong> above to apply opacity and enable/disable settings.
          </div>
        </section>

        {FIELDS.map((f) => (
          <div key={f.key}>
            <Label className="eyebrow">{f.label}</Label>
            {f.type === "textarea" ? (
              <Textarea rows={4} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`site-${f.key}`} />
            ) : (
              <Input value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`site-${f.key}`} />
            )}
            {f.key === "hero_image_url" && (
              <>
                {form.hero_image_url && <img src={mediaUrl(form.hero_image_url)} alt="hero preview" className="mt-3 w-full max-w-lg aspect-[16/9] object-cover border border-line" />}
                <label className="mt-2 inline-block text-xs border border-line px-3 py-2 hover:border-ink cursor-pointer">
                  Upload new hero image
                  <input type="file" accept="image/*" hidden onChange={async (e) => {
                    const file = e.target.files?.[0]; if (!file) return;
                    try { const url = await uploadHero(file); setForm((cur) => ({ ...cur, hero_image_url: url })); toast.success("Hero uploaded"); }
                    catch { toast.error("Upload failed"); }
                  }} />
                </label>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ServiceCardEditor({ index, value, onChange }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("kind", "service");
      const { data } = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange({ image_url: `/api/media/${data.storage_path}` });
      toast.success("Image uploaded");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upload failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-line bg-white" data-testid={`service-editor-${value.key}`}>
      <div className="aspect-[4/5] bg-sand overflow-hidden">
        {value.image_url ? (
          <img src={mediaUrl(value.image_url)} alt={value.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-xs uppercase tracking-widest text-stone">No image</div>
        )}
      </div>
      <div className="p-4 space-y-3">
        <div className="font-mono text-[10px] uppercase tracking-widest text-stone">#{index + 1} · {value.key}</div>
        <div>
          <Label className="eyebrow">Title</Label>
          <Input value={value.title || ""} onChange={(e) => onChange({ title: e.target.value })} data-testid={`service-title-${value.key}`} />
        </div>
        <div>
          <Label className="eyebrow">Tagline</Label>
          <Input value={value.tagline || ""} onChange={(e) => onChange({ tagline: e.target.value })} data-testid={`service-tagline-${value.key}`} />
        </div>
        <div>
          <Label className="eyebrow">Description</Label>
          <Textarea rows={3} value={value.body || ""} onChange={(e) => onChange({ body: e.target.value })} data-testid={`service-body-${value.key}`} />
        </div>
        <div>
          <Label className="eyebrow">Image URL</Label>
          <Input value={value.image_url || ""} onChange={(e) => onChange({ image_url: e.target.value })} placeholder="Paste URL…" data-testid={`service-image-${value.key}`} />
          <label className="mt-2 inline-flex items-center gap-2 text-xs cursor-pointer border border-line px-3 py-2 hover:border-ink" data-testid={`service-upload-${value.key}`}>
            <Upload size={12} /> {busy ? "Uploading…" : "Upload image"}
            <input type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files?.[0])} />
          </label>
        </div>
      </div>
    </div>
  );
}


function BrandingUploader({ label, value, onChange, testId, previewBg, accept, recommend, small }) {
  const [busy, setBusy] = useState(false);
  const doUpload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("kind", testId);
      const { data } = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `/api/media/${data.storage_path}`;
      onChange(url);
      toast.success(`${label} uploaded`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upload failed"); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <Label className="eyebrow">{label}</Label>
      <div className={`mt-2 border border-line ${previewBg} ${small ? "h-24 w-24" : "h-28"} flex items-center justify-center overflow-hidden`}>
        {value ? (
          <img src={mediaUrl(value)} alt={label} className={`${small ? "max-w-[80%] max-h-[80%]" : "max-h-16"} object-contain`} />
        ) : (
          <span className="font-mono text-[10px] uppercase tracking-widest text-stone">Preview</span>
        )}
      </div>
      <div className="mt-3 flex items-center gap-2">
        <Input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Paste URL…"
          className="flex-1"
          data-testid={`branding-${testId}-url`}
        />
        <label className="text-xs cursor-pointer border border-line px-3 py-2 hover:border-ink inline-flex items-center gap-2 whitespace-nowrap" data-testid={`branding-${testId}-upload`}>
          <Upload size={12} /> {busy ? "…" : "Upload"}
          <input type="file" hidden accept={accept} onChange={(e) => doUpload(e.target.files?.[0])} />
        </label>
        {value && (
          <button
            type="button"
            onClick={() => onChange("")}
            className="text-xs border border-line px-3 py-2 hover:border-destructive hover:text-destructive"
            data-testid={`branding-${testId}-remove`}
          >
            Remove
          </button>
        )}
      </div>
      <p className="text-xs text-stone mt-1.5">{recommend}</p>
    </div>
  );
}
